import React, { useEffect, useState } from 'react';
import { ReactComponent as TicketIcon } from '../assets/icons/ticket-entries.svg';
import { boostCalloutEndsAt, dreamWeek, networkBannerBoost } from '../utils/dreamBoost';
import '../styles/DreamBoost.css';

// The small boost pieces the embed draws beside a prize's entries (dreamBoost.js):
// the "5X" chip, the "Purchases today earn 5X entries" tile, the "earning faster"
// callout and the Monday-to-Sunday strip of boost days.

export function BoostMark({ multiplier }) {
  return (
    <span className="dream-boost-mark-chip">
      <TicketIcon aria-hidden="true" />
      <span>{`${multiplier}X`}</span>
    </span>
  );
}

/** The chip beside "Your entries" while a school-wide boost runs; null otherwise. */
export function BoostChip({ summary, now }) {
  const boost = networkBannerBoost(summary, now);
  if (!boost) return null;
  return <BoostMark multiplier={boost.multiplier} />;
}

/** "Purchases today earn 5X entries" under the entries; null without a boost or at the entry cap. */
export function BoostTile({ summary, now }) {
  const boost = networkBannerBoost(summary, now);
  if (!boost || summary.at_entry_cap === true) return null;
  return (
    <div className="dream-boost-tile">
      <BoostMark multiplier={boost.multiplier} />
      <span className="dream-boost-tile-text">{`Purchases today earn ${boost.multiplier}X entries`}</span>
    </div>
  );
}

/** "You're earning faster today." while any boost runs and the fan is below the cap. */
export function BoostCallout({ summary, now }) {
  const endsAt = boostCalloutEndsAt(summary, now);
  if (!endsAt) return null;
  return <div className="dream-boost-callout">You’re earning faster today.</div>;
}

/** This week's boost days; null when none of the seven days has a boost. */
export function DreamWeek({ summary, now }) {
  const week = dreamWeek(summary, now);
  if (!week || !week.count) return null;
  const countLabel = week.count === 1 ? '1 boost day' : `${week.count} boost days`;
  return (
    <section className="dream-week" aria-label="This week's boosts">
      <div className="dream-week-head">
        <div className="dream-week-title">This week</div>
        <div className="dream-week-count">{countLabel}</div>
      </div>
      <div className="dream-week-days">
        {week.days.map((day, index) => {
          let kind = 'empty';
          if (day.today) kind = 'today';
          else if (day.multiplier && day.multiplier === week.peak) kind = 'peak';
          else if (day.multiplier) kind = 'boost';
          return (
            <div className={`dream-week-day is-${kind}`} key={`${day.label}-${index}`}>
              <span className="dream-week-name">{day.label}</span>
              <span className="dream-week-mark">
                {day.multiplier ? (
                  <>
                    <TicketIcon aria-hidden="true" />
                    <span className="dream-week-mult">{`${day.multiplier}X`}</span>
                  </>
                ) : (
                  <span className="dream-week-dot" />
                )}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** A clock for the boost pieces: ticks once a second while a boost is running. */
export function useBoostClock(summary) {
  const [now, setNow] = useState(() => Date.now());
  const running = !!networkBannerBoost(summary, now) || !!boostCalloutEndsAt(summary, now);
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  return now;
}
